const normalizeProductSearchInput = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const normalizeProductSearchText = (value) =>
  normalizeProductSearchInput(value).trim();

export const PRODUCT_SEARCH_SUGGESTION_LIMIT = 5;
export const PRODUCT_SEARCH_MODE_SUGGESTIVE = 'suggestive';
export const PRODUCT_SEARCH_MODE_STRICT = 'strict';
export const PRODUCT_SEARCH_MATCH_EXACT = 'exact';
export const PRODUCT_SEARCH_MATCH_SUGGESTED = 'suggested';
export const PRODUCT_SEARCH_MATCH_NONE = 'none';

const PRODUCT_SEARCH_MODES = [
  PRODUCT_SEARCH_MODE_SUGGESTIVE,
  PRODUCT_SEARCH_MODE_STRICT,
];

export const normalizeProductSearchMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return PRODUCT_SEARCH_MODES.includes(normalized)
    ? normalized
    : PRODUCT_SEARCH_MODE_SUGGESTIVE;
};

const markSearchTokens = (tokens, partialLastToken = false) => {
  Object.defineProperty(tokens, 'partialLastToken', {
    value: Boolean(partialLastToken && tokens.length),
    enumerable: false,
    configurable: true,
  });
  return tokens;
};

export const getProductSearchWords = (value) =>
  normalizeProductSearchText(value)
    .split(/[^a-z0-9]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

export const tokenizeProductSearchQuery = (query) => {
  const tokens = getProductSearchWords(query);
  const normalizedInput = normalizeProductSearchInput(query);
  return markSearchTokens(tokens, /[a-z0-9]$/.test(normalizedInput));
};

const normalizeSearchTokens = (queryOrTokens) => (
  Array.isArray(queryOrTokens)
    ? markSearchTokens(
      queryOrTokens.map(normalizeProductSearchText).filter(Boolean),
      queryOrTokens.partialLastToken
    )
    : tokenizeProductSearchQuery(queryOrTokens)
);

const wordsMatchSearchTokens = (words, queryOrTokens) => {
  const tokens = normalizeSearchTokens(queryOrTokens);
  if (!tokens.length) return true;
  if (!words.length) return false;
  const partialIndex = tokens.partialLastToken ? tokens.length - 1 : -1;
  return tokens.every((token, index) => {
    if (index === partialIndex) {
      return words.some((word) => word.startsWith(token));
    }
    return words.includes(token);
  });
};

const compactFields = (values) => (
  values
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
);

const getPriceBarcodeFields = (prices = []) => (
  (Array.isArray(prices) ? prices : []).flatMap((price) => compactFields([
    price?.barcode,
    price?.shortBarcode,
  ]))
);

export const getProductSearchFields = (product) => compactFields([
  product?.productName,
  product?.name,
  product?.barcode,
  product?.shortBarcode,
  product?.sku,
  product?.code,
  product?.productCode,
]);

export const getVarietySearchFields = (variety) => compactFields([
  variety?.varietyName,
  variety?.name,
  variety?.barcode,
  variety?.shortBarcode,
  variety?.sku,
  variety?.code,
  ...getPriceBarcodeFields(variety?.VarietyPrices),
]);

export const textMatchesSearchTerms = (value, queryOrTokens) => {
  const words = getProductSearchWords(value);
  return wordsMatchSearchTokens(words, queryOrTokens);
};

export const fieldsMatchStrictProductSearchTerms = (fields, queryOrTokens) => {
  const words = compactFields(fields).flatMap(getProductSearchWords);
  return wordsMatchSearchTokens(words, queryOrTokens);
};

export const fieldsIncludeProductSearchTerms = (fields, queryOrTokens) => {
  const tokens = normalizeSearchTokens(queryOrTokens);
  if (!tokens.length) return true;
  const normalizedValue = compactFields(fields)
    .map(normalizeProductSearchText)
    .join(' ');
  if (!normalizedValue) return false;
  return tokens.every((token) => normalizedValue.includes(token));
};

export const fieldsMatchProductSearchTerms = (fields, queryOrTokens, options = {}) => {
  const searchMode = normalizeProductSearchMode(options.searchMode ?? options.mode);
  const strictMatch = fieldsMatchStrictProductSearchTerms(fields, queryOrTokens);
  if (searchMode === PRODUCT_SEARCH_MODE_STRICT || strictMatch) return strictMatch;
  return fieldsIncludeProductSearchTerms(fields, queryOrTokens);
};

export const productOwnFieldsMatchProductSearch = (product, queryOrTokens) =>
  fieldsMatchProductSearchTerms(getProductSearchFields(product), queryOrTokens);

export const varietyFieldsMatchProductSearch = (variety, queryOrTokens) =>
  fieldsMatchProductSearchTerms(getVarietySearchFields(variety), queryOrTokens);

const productVarietyFieldsMatchProductSearch = (product, variety, queryOrTokens, options = {}) =>
  fieldsMatchProductSearchTerms([
    ...getProductSearchFields(product),
    ...getVarietySearchFields(variety),
  ], queryOrTokens, options);

export const productMatchesProductSearch = (product, queryOrTokens, options = {}) => {
  const tokens = normalizeSearchTokens(queryOrTokens);
  if (!tokens.length) return true;
  if (fieldsMatchProductSearchTerms(getProductSearchFields(product), tokens, options)) return true;
  return (product?.Varieties || []).some((variety) =>
    productVarietyFieldsMatchProductSearch(product, variety, tokens, options)
  );
};

export const getProductSearchMatch = (product, queryOrTokens, options = {}) => {
  const tokens = normalizeSearchTokens(queryOrTokens);
  const searchMode = normalizeProductSearchMode(options.searchMode ?? options.mode);
  const varieties = Array.isArray(options.varieties)
    ? options.varieties
    : (product?.Varieties || []);
  if (!tokens.length) {
    return {
      matches: true,
      exactMatches: true,
      suggestedMatches: false,
      productMatches: true,
      matchingVarieties: varieties,
      suggestedProductMatches: false,
      suggestedMatchingVarieties: [],
      matchType: PRODUCT_SEARCH_MATCH_EXACT,
    };
  }

  const productFields = getProductSearchFields(product);
  const productMatches = fieldsMatchStrictProductSearchTerms(productFields, tokens);
  const matchingVarieties = productMatches
    ? varieties
    : varieties.filter((variety) =>
      productVarietyFieldsMatchProductSearch(product, variety, tokens, {
        searchMode: PRODUCT_SEARCH_MODE_STRICT,
      })
    );
  const exactMatches = productMatches || matchingVarieties.length > 0;
  const allowSuggestions = searchMode !== PRODUCT_SEARCH_MODE_STRICT;
  const suggestedProductMatches = allowSuggestions
    && !productMatches
    && fieldsIncludeProductSearchTerms(productFields, tokens);
  const suggestedMatchingVarieties = allowSuggestions && !productMatches
    ? varieties.filter((variety) => {
      if (productVarietyFieldsMatchProductSearch(product, variety, tokens, {
        searchMode: PRODUCT_SEARCH_MODE_STRICT,
      })) {
        return false;
      }
      return fieldsIncludeProductSearchTerms([
        ...productFields,
        ...getVarietySearchFields(variety),
      ], tokens);
    })
    : [];
  const suggestedMatches = suggestedProductMatches || suggestedMatchingVarieties.length > 0;

  return {
    matches: exactMatches || suggestedMatches,
    exactMatches,
    suggestedMatches,
    productMatches,
    matchingVarieties,
    suggestedProductMatches,
    suggestedMatchingVarieties,
    matchType: exactMatches
      ? PRODUCT_SEARCH_MATCH_EXACT
      : suggestedMatches
        ? PRODUCT_SEARCH_MATCH_SUGGESTED
        : PRODUCT_SEARCH_MATCH_NONE,
  };
};

const getProductResultKey = (product) =>
  String(product?.id ?? product?.productName ?? product?.name ?? '');

const getVarietyResultKey = (product, variety) =>
  `${getProductResultKey(product)}:${String(variety?.id ?? variety?.varietyName ?? variety?.name ?? '')}`;

const pushUniqueProductResult = (target, seen, product) => {
  const key = getProductResultKey(product);
  if (!key || seen.has(key)) return;
  seen.add(key);
  target.push(product);
};

const pushUniqueVarietyResult = (target, seen, product, variety) => {
  const key = getVarietyResultKey(product, variety);
  if (!key || seen.has(key)) return;
  seen.add(key);
  target.push({ product, variety });
};

export const splitProductSearchResults = (products = [], queryOrTokens, options = {}) => {
  const tokens = normalizeSearchTokens(queryOrTokens);
  const availableProducts = Array.isArray(products) ? products : [];
  if (!tokens.length) {
    return {
      exactProducts: availableProducts,
      suggestedProducts: [],
      exactVarietyResults: [],
      suggestedVarietyResults: [],
      matchedProducts: availableProducts,
    };
  }

  const exactProducts = [];
  const suggestedProducts = [];
  const exactVarietyResults = [];
  const suggestedVarietyResults = [];
  const seenExactProducts = new Set();
  const seenSuggestedProducts = new Set();
  const seenExactVarieties = new Set();
  const seenSuggestedVarieties = new Set();
  const searchMode = normalizeProductSearchMode(options.searchMode ?? options.mode);

  availableProducts.forEach((product) => {
    const varieties = typeof options.getVarieties === 'function'
      ? options.getVarieties(product)
      : product?.Varieties;
    const match = getProductSearchMatch(product, tokens, {
      ...options,
      searchMode,
      varieties: Array.isArray(varieties) ? varieties : [],
    });
    if (match.exactMatches) {
      pushUniqueProductResult(exactProducts, seenExactProducts, product);
      match.matchingVarieties.forEach((variety) =>
        pushUniqueVarietyResult(exactVarietyResults, seenExactVarieties, product, variety)
      );
    } else if (match.suggestedMatches) {
      pushUniqueProductResult(suggestedProducts, seenSuggestedProducts, product);
      match.suggestedMatchingVarieties.forEach((variety) =>
        pushUniqueVarietyResult(suggestedVarietyResults, seenSuggestedVarieties, product, variety)
      );
    }
  });

  return {
    exactProducts,
    suggestedProducts,
    exactVarietyResults,
    suggestedVarietyResults,
    matchedProducts: [...exactProducts, ...suggestedProducts],
  };
};

export const filterProductSearchResults = (products = [], queryOrTokens, options = {}) =>
  splitProductSearchResults(products, queryOrTokens, options).matchedProducts;

export const orderFieldSearchResults = (items = [], queryOrTokens, getFields, options = {}) => {
  const tokens = normalizeSearchTokens(queryOrTokens);
  const list = Array.isArray(items) ? items : [];
  if (!tokens.length) return list;
  const searchMode = normalizeProductSearchMode(options.searchMode ?? options.mode);
  const exact = [];
  const suggested = [];

  list.forEach((item) => {
    const fields = typeof getFields === 'function' ? getFields(item) : item;
    if (fieldsMatchStrictProductSearchTerms(fields, tokens)) {
      exact.push(item);
      return;
    }
    if (
      searchMode !== PRODUCT_SEARCH_MODE_STRICT
      && fieldsIncludeProductSearchTerms(fields, tokens)
    ) {
      suggested.push(item);
    }
  });

  return [...exact, ...suggested];
};

const collectSuggestionValues = (product, includeBarcodes) => {
  const suggestions = [];
  const productFields = getProductSearchFields(product);
  compactFields([product?.productName, product?.name]).forEach((value) => {
    suggestions.push({ value, priority: 0, searchFields: productFields });
  });
  if (includeBarcodes) {
    compactFields([
      product?.barcode,
      product?.shortBarcode,
      product?.sku,
      product?.code,
      product?.productCode,
    ]).forEach((value) => {
      suggestions.push({ value, priority: 2, searchFields: productFields });
    });
  }

  (product?.Varieties || []).forEach((variety) => {
    const productVarietyFields = [
      ...productFields,
      ...getVarietySearchFields(variety),
    ];
    compactFields([variety?.varietyName, variety?.name]).forEach((value) => {
      suggestions.push({ value, priority: 1, searchFields: productVarietyFields });
    });
    if (includeBarcodes) {
      compactFields([
        variety?.barcode,
        variety?.shortBarcode,
        variety?.sku,
        variety?.code,
        ...getPriceBarcodeFields(variety?.VarietyPrices),
      ]).forEach((value) => {
        suggestions.push({ value, priority: 2, searchFields: productVarietyFields });
      });
    }
  });

  return suggestions;
};

const getWordStartScore = (normalizedValue, tokens) => {
  const words = getProductSearchWords(normalizedValue);
  if (!tokens.length || !words.length) return 0;
  return tokens.every((token) => words.some((word) => word.startsWith(token))) ? -40 : 0;
};

const getSuggestionScore = ({ value, priority = 0, historyIndex = null }, queryTokens, normalizedQuery) => {
  const normalizedValue = normalizeProductSearchText(value);
  let score = priority * 100;

  if (Number.isInteger(historyIndex)) {
    score -= 300;
    score += Math.min(historyIndex, 25);
  }

  if (normalizedQuery) {
    if (normalizedValue === normalizedQuery) {
      score -= 100;
    } else if (normalizedValue.startsWith(normalizedQuery)) {
      score -= 70;
    } else if (normalizedValue.includes(normalizedQuery)) {
      score -= 50;
    }
    score += getWordStartScore(normalizedValue, queryTokens);
  }

  score += Math.min(normalizedValue.length, 120) / 1000;
  return score;
};

export const buildProductSearchSuggestions = (products = [], options = {}) => {
  const {
    includeBarcodes = true,
    limit = PRODUCT_SEARCH_SUGGESTION_LIMIT,
    query = '',
    history = [],
    searchMode = PRODUCT_SEARCH_MODE_SUGGESTIVE,
    suggestionValues = [],
  } = options;
  const availableProducts = Array.isArray(products) ? products : [];
  const availableSuggestionValues = Array.isArray(suggestionValues) ? suggestionValues : [];
  const candidatesByValue = new Map();
  const queryTokens = tokenizeProductSearchQuery(query);
  const normalizedQuery = normalizeProductSearchText(query);

  const addCandidate = (candidate) => {
    const normalized = normalizeProductSearchText(candidate?.value);
    if (!normalized) return;
    const existing = candidatesByValue.get(normalized);
    const searchFields = [
      ...(existing?.searchFields || []),
      ...(candidate?.searchFields || [candidate.value]),
    ];
    if (
      queryTokens.length
      && !fieldsMatchProductSearchTerms(searchFields, queryTokens, { searchMode })
    ) {
      return;
    }
    if (!existing) {
      candidatesByValue.set(normalized, {
        ...candidate,
        searchFields,
      });
      return;
    }
    const nextPriority = Math.min(existing.priority ?? 0, candidate.priority ?? 0);
    candidatesByValue.set(normalized, {
      ...existing,
      ...candidate,
      priority: nextPriority,
      searchFields,
      historyIndex: Number.isInteger(existing.historyIndex)
        ? existing.historyIndex
        : candidate.historyIndex,
    });
  };

  const historyMatchesAvailableProduct = (value) => {
    const historyTokens = tokenizeProductSearchQuery(value);
    if (!historyTokens.length) return false;
    if (
      availableSuggestionValues.some((entry) =>
        fieldsMatchProductSearchTerms([entry], historyTokens, { searchMode })
      )
    ) {
      return true;
    }
    return availableProducts.some((product) =>
      productMatchesProductSearch(product, historyTokens, { searchMode })
    );
  };

  availableSuggestionValues.forEach((value) => {
    addCandidate({ value, priority: 0 });
  });

  availableProducts.forEach((product) => {
    collectSuggestionValues(product, includeBarcodes).forEach(addCandidate);
  });

  (Array.isArray(history) ? history : []).forEach((value, historyIndex) => {
    if (historyMatchesAvailableProduct(value)) {
      addCandidate({ value, priority: -2, historyIndex });
    }
  });

  return Array.from(candidatesByValue.values())
    .sort((left, right) => {
      const leftScore = getSuggestionScore(left, queryTokens, normalizedQuery);
      const rightScore = getSuggestionScore(right, queryTokens, normalizedQuery);
      if (leftScore !== rightScore) return leftScore - rightScore;
      return String(left.value).localeCompare(String(right.value));
    })
    .slice(0, limit)
    .map(({ value }) => value);
};
